import { useState, useMemo, useRef, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  RefreshCw,
  TrendingUp,
  WalletCards,
  Clock3,
  Gift,
  ReceiptText,
  BarChart3,
  Package,
  Tags,
  Award,
  ArrowDownRight,
  Percent,
  CalendarDays,
} from "lucide-react";
import { useReportesAnio, useReportesTodos } from "@/features/reports";
import { cn } from "@/lib/utils";
import type {
  ReportesMes, ReportesTotales, ReportesAnioResumen, ReportesProducto, ReportesCategoriaGasto,
} from "@cold-crew/shared";

export const Route = createFileRoute("/_app/reportes/")({
  component: ReportesPage,
});

// ─── Helpers de formato ────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency: "MXN", maximumFractionDigits: 0,
  }).format(n || 0);
}

function pct(n: number) {
  return `${(n || 0).toFixed(1)}%`;
}

// ─── Paleta de marca (misma que Dashboard) ────────────────────────────────────

const C = {
  ingresos:   "#2F6D46",
  gastos:     "#B74A2E",
  cafeFuerte: "#090909",
  cafeBajito: "#B07A4F",
  gold:       "#FF6500",
};

// Misma lógica de color por producto usada en el Dashboard, para consistencia visual
function colorProducto(nombre: string) {
  const n = nombre.toLowerCase();
  if (n.includes("litro"))  return C.cafeFuerte;
  if (n.includes("granel")) return "#8B5E3C";
  return C.cafeBajito;
}

const GRADIENT = "#090909";

const GASTO_COLORS = ["#090909", "#6B4A32", "#8B5E3C", "#B07A4F", "#DEDCD5", "#FF6500"];

// ─── Componentes visuales locales ─────────────────────────────────────────────

function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("card-premium overflow-hidden", className)}>{children}</div>;
}

function CardHead({ title, icon: Icon }: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-center gap-2" style={{ background: GRADIENT }}>
      <div className="flex items-center gap-2 px-3.5 py-2.5">
        {Icon ? <Icon className="size-3.5 text-white" /> : <span className="h-3.5 w-1 rounded-full bg-white" />}
        <h3 className="text-xs font-extrabold text-white tracking-tight">{title}</h3>
      </div>
    </div>
  );
}

function Kpi({
  label, value, accent = "#111111", icon: Icon, meta,
}: {
  label: string;
  value: string;
  accent?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  meta: string;
}) {
  return (
    <div className="card-premium min-h-[108px] p-4 flex items-stretch justify-between gap-3">
      <div className="min-w-0 flex flex-col justify-between">
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground leading-tight">{label}</p>
          <p className="mt-1 text-[clamp(1.35rem,2vw,1.75rem)] font-extrabold tabular-nums tracking-tight" style={{ color: accent }}>
            {value}
          </p>
        </div>
        <p className="mt-2 text-[10px] font-semibold" style={{ color: accent }}>{meta}</p>
      </div>
      <div
        className="self-stretch min-w-[72px] w-[26%] max-w-[104px] rounded-2xl flex items-center justify-center"
        style={{ background: `${accent}0D` }}
      >
        <Icon className="size-7" style={{ color: accent }} />
      </div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
      {label}
    </div>
  );
}

// ── Gráfica de líneas interactiva — SVG propio, sin librerías ──────────────────
// Incluye: cuadrícula de referencia, puntos por mes, etiquetas de dato sobre la
// serie principal, línea punteada para "Vendido" (evita que tape a "Cobrado"),
// y tooltip con el valor grande al pasar el cursor sobre un mes.

interface LineSeries { label: string; color: string; data: number[] }

function fmtCompact(n: number) {
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

function EvolucionChart({
  labels,
  series,
  year,
}: {
  labels: string[];
  series: LineSeries[];
  year?: number;
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const hayDatos = series.some((s) => s.data.some((v) => v > 0));

  if (!hayDatos) {
    return <EmptyState label="Sin información para este periodo" />;
  }

  const W = 1000;
  const H = 300;

  const PAD_L = 64;
  const PAD_R = 24;
  const PAD_T = 20;
  const PAD_B = 38;

  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const maxRaw = Math.max(1, ...series.flatMap((s) => s.data));
  const maxVal = Math.ceil(maxRaw / 4000) * 4000 || 4000;

  const n = labels.length;
  const stepX = n > 1 ? plotW / (n - 1) : 0;

  const xAt = (i: number) => PAD_L + i * stepX;

  const yAt = (v: number) =>
    PAD_T + (1 - v / maxVal) * plotH;

  const linePoints = (data: number[]) =>
    data.map((v, i) => `${xAt(i)},${yAt(v)}`).join(" ");

  const areaPoints = (data: number[]) => {
    const firstX = xAt(0);
    const lastX = xAt(data.length - 1);
    const bottom = H - PAD_B;

    return [
      `${firstX},${bottom}`,
      ...data.map((v, i) => `${xAt(i)},${yAt(v)}`),
      `${lastX},${bottom}`,
    ].join(" ");
  };

  const gridValues = [
    0,
    maxVal / 3,
    (maxVal / 3) * 2,
    maxVal,
  ];

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect();

    if (!rect || n === 0) return;

    const relativeX =
      ((e.clientX - rect.left) / rect.width) * W;

    const index = Math.round(
      (relativeX - PAD_L) / (stepX || 1)
    );

    setHoverIdx(
      Math.min(n - 1, Math.max(0, index))
    );
  }

  const tooltipLeft =
    hoverIdx !== null
      ? Math.min(
          82,
          Math.max(
            18,
            (xAt(hoverIdx) / W) * 100
          )
        )
      : 0;

  const tooltipTop =
    hoverIdx !== null
      ? Math.max(
          6,
          (Math.min(
            ...series.map((s) =>
              yAt(s.data[hoverIdx] ?? 0)
            )
          ) /
            H) *
            100 -
            6
        )
      : 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Leyenda superior, como el mockup */}
      <div className="mb-2 flex flex-wrap items-center gap-5 px-1">
        {series.map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-2"
          >
            <span
              className="size-2.5 rounded-full"
              style={{ backgroundColor: s.color }}
            />

            <span className="text-xs font-medium text-muted-foreground">
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* La gráfica ocupa todo el espacio disponible */}
      <div className="relative min-h-[260px] flex-1">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full cursor-crosshair"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient
              id="ventasArea"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="#111111"
                stopOpacity="0.14"
              />
              <stop
                offset="100%"
                stopColor="#111111"
                stopOpacity="0"
              />
            </linearGradient>

            <linearGradient
              id="cobradoArea"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={C.ingresos}
                stopOpacity="0.18"
              />
              <stop
                offset="100%"
                stopColor={C.ingresos}
                stopOpacity="0"
              />
            </linearGradient>

            <linearGradient
              id="gastosArea"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={C.gastos}
                stopOpacity="0.16"
              />
              <stop
                offset="100%"
                stopColor={C.gastos}
                stopOpacity="0"
              />
            </linearGradient>
          </defs>

          {/* Líneas horizontales */}
          {gridValues.map((value) => {
            const y = yAt(value);

            return (
              <g key={value}>
                <line
                  x1={PAD_L}
                  y1={y}
                  x2={W - PAD_R}
                  y2={y}
                  stroke="#E8E5DF"
                  strokeWidth="1"
                />

                <text
                  x={PAD_L - 14}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#6F6A63"
                >
                  {fmtCompact(value)}
                </text>
              </g>
            );
          })}

          {/* Áreas degradadas */}
          {series.map((s, index) => {
            const fill =
              index === 0
                ? "url(#ventasArea)"
                : index === 1
                  ? "url(#cobradoArea)"
                  : "url(#gastosArea)";

            return (
              <polygon
                key={`area-${s.label}`}
                points={areaPoints(s.data)}
                fill={fill}
              />
            );
          })}

          {/* Línea vertical de hover */}
          {hoverIdx !== null && (
            <line
              x1={xAt(hoverIdx)}
              y1={PAD_T}
              x2={xAt(hoverIdx)}
              y2={H - PAD_B}
              stroke="#4F4A44"
              strokeWidth="1.25"
              strokeDasharray="6 5"
            />
          )}

          {/* Líneas */}
          {series.map((s) => (
            <polyline
              key={s.label}
              points={linePoints(s.data)}
              fill="none"
              stroke={s.color}
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Puntos */}
          {series.map((s) =>
            s.data.map((v, i) => {
              const active = hoverIdx === i;

              return (
                <g key={`${s.label}-${i}`}>
                  {active && (
                    <circle
                      cx={xAt(i)}
                      cy={yAt(v)}
                      r="9"
                      fill={s.color}
                      opacity="0.13"
                    />
                  )}

                  <circle
                    cx={xAt(i)}
                    cy={yAt(v)}
                    r={active ? 5 : 3.5}
                    fill={s.color}
                    stroke="#FFFFFF"
                    strokeWidth={active ? 2 : 1}
                  />
                </g>
              );
            })
          )}

          {/* Meses */}
          {labels.map((label, i) => (
            <text
              key={`${label}-${i}`}
              x={xAt(i)}
              y={H - 10}
              textAnchor="middle"
              fontSize="12"
              fill="#6F6A63"
            >
              {label}
            </text>
          ))}
        </svg>

        {/* Tooltip igual al mockup */}
        {hoverIdx !== null && (
          <div
            className="
              pointer-events-none absolute z-20
              min-w-[175px]
              -translate-x-1/2
              rounded-xl
              border border-[#E4E0D9]
              bg-white
              px-3.5 py-3
              shadow-[0_10px_30px_rgba(0,0,0,0.12)]
            "
            style={{
              left: `${tooltipLeft}%`,
              top: `${tooltipTop}%`,
            }}
          >
            <p className="mb-2 text-sm font-extrabold text-foreground">
              {labels[hoverIdx]}{year ? ` ${year}` : ""}
            </p>

            <div className="space-y-1.5">
              {series.map((s) => (
                <div
                  key={s.label}
                  className="grid grid-cols-[10px_1fr_auto] items-center gap-2"
                >
                  <span
                    className="size-2.5 rounded-full"
                    style={{
                      backgroundColor: s.color,
                    }}
                  />

                  <span className="text-xs text-muted-foreground">
                    {s.label}:
                  </span>

                  <span className="text-xs font-bold tabular-nums text-foreground">
                    {fmt(s.data[hoverIdx] ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Lista de barras horizontales — nombre, monto y % por fila ──────────────────

interface DonutItem { label: string; value: number; porcentaje: number; color: string }

function BarListCard({
  items,
  totalValue,
  totalLabel,
  variant = "ventas",
}: {
  items: DonutItem[];
  totalValue: string;
  totalLabel: string;
  variant?: "ventas" | "gastos";
}) {
  if (items.length === 0) {
    return <EmptyState label="Sin información para este periodo" />;
  }

  const isVentas = variant === "ventas";

  return (
    <div className="flex h-full flex-col">
      {/* Resumen superior */}
      <div
        className={cn(
  "flex items-center justify-between gap-4 rounded-2xl border border-[#E9E4DC] bg-gradient-to-r from-[#FBF7F0] via-[#FCFAF6] to-white",
  isVentas
    ? "mb-3 px-4 py-3.5 sm:px-5 sm:py-4"
    : "mb-2 px-4 py-2.5 sm:px-4 sm:py-2.5"
)}
      >
        <div className="min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#7A7168]">
            {isVentas ? "Ventas del periodo" : "Gastos del periodo"}
          </p>

          <div className="mt-1 flex items-center gap-2">
            {isVentas
              ? <Package className="size-4 text-[#B07A4F]" />
              : <Tags className="size-4 text-[#B07A4F]" />}

            <p className="text-xs font-semibold text-muted-foreground">
              {items.length}{" "}
              {isVentas
                ? items.length === 1 ? "producto vendido" : "productos vendidos"
                : items.length === 1 ? "categoría" : "categorías"}
            </p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p
  className={cn(
    "font-black tracking-tight text-foreground",
    isVentas
      ? "text-xl sm:text-2xl"
      : "text-lg sm:text-xl"
  )}
>
  {totalValue}
</p>

          <p className="mt-0.5 text-[10px] font-semibold text-muted-foreground">
            {totalLabel}
          </p>
        </div>
      </div>

      {/* Ranking: en escritorio llena uniformemente toda la tarjeta */}
      <div
        className={cn(
          "grid flex-1 grid-cols-1",
          isVentas
  ? "gap-3 lg:grid-rows-3"
  : "gap-1.5 lg:grid-rows-6"
        )}
      >
        {items.map((it, index) => {
          const isLeader = index === 0;

          return (
            <div
              key={it.label}
              className={cn(
                "relative flex items-center gap-3 overflow-hidden rounded-2xl border px-3.5 transition-all",
                isVentas
  ? "min-h-[96px] py-3.5 lg:min-h-0 lg:h-full lg:px-4 lg:py-4"
  : "min-h-[54px] py-1.5 lg:min-h-0 lg:h-full lg:py-1.5",
                isLeader
                  ? "border-[#E5D8C9] bg-gradient-to-r from-[#FBF4EA] via-[#FCF8F2] to-white"
                  : "border-[#ECE9E4] bg-[#FCFBF9]"
              )}
            >
              {/* Ranking */}
              <div
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-xl text-xs font-black",
                  isVentas && "lg:size-12 lg:text-sm",
                  isLeader
                    ? "bg-[#B07A4F] text-white shadow-sm"
                    : "bg-[#F1EFEB] text-[#554E47]"
                )}
              >
                {String(index + 1).padStart(2, "0")}
              </div>

              {/* Información */}
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={cn("truncate text-sm font-extrabold text-foreground", isVentas && "lg:text-base")}>
                        {it.label}
                      </p>

                      {isLeader && (
                        <span
                          className="
                            rounded-full bg-[#F1E4D4]
                            px-2 py-0.5
                            text-[9px] font-extrabold uppercase
                            tracking-[0.08em] text-[#8B5E3C]
                          "
                        >
                          Mayor participación
                        </span>
                      )}
                    </div>

                    <p className={cn(
                      "mt-0.5 text-[10px] font-medium text-muted-foreground",
                      isVentas && "lg:text-xs"
                    )}>
                      {pct(it.porcentaje)} {isVentas ? "del total vendido" : "del gasto total"}
                    </p>
                  </div>

                  <p className={cn(
                    "shrink-0 text-sm font-black tabular-nums text-foreground sm:text-base",
                    isVentas && "lg:text-xl"
                  )}>
                    {fmt(it.value)}
                  </p>
                </div>

                {/* Barra */}
                <div className={cn(
                  "relative h-3 overflow-hidden rounded-full bg-[#EDEAE5]",
                  isVentas && "lg:h-3.5"
                )}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, Math.max(0, it.porcentaje))}%`,
                      background: it.color,
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Selector de año — mismo tamaño que los demás selects de la app ─────────────

function YearSelector({ value, years, onChange }: {
  value: number | "todos"; years: number[]; onChange: (v: number | "todos") => void;
}) {
  return (
    <select
      value={String(value)}
      onChange={(e) => onChange(e.target.value === "todos" ? "todos" : Number(e.target.value))}
      className="h-8 rounded-lg border border-input bg-card px-2 text-xs font-medium focus:outline-none cursor-pointer"
    >
      <option value="todos">Todos los años</option>
      {years.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

// ─── Tabla mensual ─────────────────────────────────────────────────────────────

const TABLE_HEADERS = [
  "Mes", "Vendido", "Cobrado", "Por cobrar", "Regalos $", "Gastos",
  "Utilidad", "Pedidos", "Ticket prom.", "% Cobranza",
];

function TablaMensual({ meses, totales, anio }: {
  meses: ReportesMes[]; totales: ReportesTotales | null; anio: number;
}) {
  return (
    <Card>
      <CardHead title="Resumen por mes" />
      <div className="w-full overflow-x-auto overflow-y-visible scrollbar-thin">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="sticky top-0 z-20">
            <tr style={{ background: GRADIENT }}>
              {TABLE_HEADERS.map((h) => (
                <th key={h} className={cn(
                  "px-4 py-3 text-xs font-medium text-white/80 uppercase tracking-wide whitespace-nowrap",
                  h === "Mes" ? "text-left sticky left-0 z-30 bg-[#090909]" : "text-right",
                )}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {meses.map((m) => (
              <tr key={m.mes} className="hover:bg-muted/40 transition-colors">
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap sticky left-0 z-10 bg-card">{m.mes_label}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-foreground whitespace-nowrap">{fmt(m.vendido)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-foreground whitespace-nowrap">{fmt(m.cobrado)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ color: "#B5821F" }}>{fmt(m.por_cobrar)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ color: C.gold }}>{fmt(m.regalos)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ color: C.gastos }}>{fmt(m.gastos)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap" style={{ color: C.ingresos }}>{fmt(m.utilidad)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{m.pedidos}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{fmt(m.ticket_promedio)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{pct(m.pct_cobranza)}</td>
              </tr>
            ))}
          </tbody>
          {totales && (
            <tfoot>
              <tr className="border-t-2" style={{ borderColor: C.ingresos, background: "rgba(47,109,70,0.08)" }}>
                <td className="px-4 py-3 font-extrabold whitespace-nowrap sticky left-0 z-10 bg-[#eef6f0]" style={{ color: "#1f4d31" }}>TOTAL {anio}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.vendido)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.cobrado)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.por_cobrar)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.regalos)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.gastos)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.utilidad)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{totales.pedidos}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.ticket_promedio)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{pct(totales.pct_cobranza)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

// ─── Tabla comparativa (vista "Todos los años") ────────────────────────────────

const ANIOS_HEADERS = [
  "Año", "Vendido", "Cobrado", "Por cobrar", "Regalos", "Gastos",
  "Utilidad", "Pedidos", "Ticket prom.", "% Cobranza",
];

function TablaAnios({ anios, totales }: { anios: ReportesAnioResumen[]; totales: ReportesAnioResumen | null }) {
  return (
    <Card>
      <CardHead title="Comparación por año" />
      <div className="max-h-[520px] overflow-auto scrollbar-thin">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="sticky top-0 z-20">
            <tr style={{ background: GRADIENT }}>
              {ANIOS_HEADERS.map((h) => (
                <th key={h} className={cn(
                  "px-4 py-3 text-xs font-medium text-white/80 uppercase tracking-wide whitespace-nowrap",
                  h === "Año" ? "text-left sticky left-0 z-30 bg-[#090909]" : "text-right",
                )}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {anios.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Sin información para este periodo</td></tr>
            ) : anios.map((a) => (
              <tr key={a.anio} className="hover:bg-muted/40 transition-colors">
                <td className="px-4 py-2.5 font-semibold text-foreground whitespace-nowrap sticky left-0 z-10 bg-card">{a.anio}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-foreground whitespace-nowrap">{fmt(a.vendido)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-foreground whitespace-nowrap">{fmt(a.cobrado)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ color: "#B5821F" }}>{fmt(a.por_cobrar)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ color: C.gold }}>{fmt(a.regalos)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ color: C.gastos }}>{fmt(a.gastos)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap" style={{ color: C.ingresos }}>{fmt(a.utilidad)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{a.pedidos}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{fmt(a.ticket_promedio)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{pct(a.pct_cobranza)}</td>
              </tr>
            ))}
          </tbody>
          {totales && anios.length > 0 && (
            <tfoot>
              <tr className="border-t-2" style={{ borderColor: C.ingresos, background: "rgba(47,109,70,0.08)" }}>
                <td className="px-4 py-3 font-extrabold whitespace-nowrap sticky left-0 z-10 bg-[#eef6f0]" style={{ color: "#1f4d31" }}>TOTAL</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.vendido)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.cobrado)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.por_cobrar)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.regalos)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.gastos)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.utilidad)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{totales.pedidos}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{fmt(totales.ticket_promedio)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-extrabold whitespace-nowrap" style={{ color: "#1f4d31" }}>{pct(totales.pct_cobranza)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

// ─── Vista: año seleccionado ────────────────────────────────────────────────────

function VistaAnio({ anio }: { anio: number }) {
  const { data, isLoading, isError, refetch, isFetching } = useReportesAnio(anio);

  if (isLoading) return (
    <div className="flex items-center justify-center py-24 gap-2 text-sm text-muted-foreground">
      <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      Cargando reportes…
    </div>
  );
  if (isError || !data) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <p className="text-sm text-destructive">Error al cargar los reportes.</p>
      <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
    </div>
  );

  const sinDatos = data.totales.vendido === 0 && data.totales.gastos === 0 && data.totales.regalos === 0;

  const productoItems: DonutItem[] = data.ventas_por_producto.map((p: ReportesProducto) => ({
    label: p.producto_nombre, value: p.vendido, porcentaje: p.porcentaje,
    color: colorProducto(p.producto_nombre),
  }));
  const categoriaItems: DonutItem[] = data.gastos_por_categoria.map((c: ReportesCategoriaGasto, i: number) => ({
    label: c.label, value: c.monto, porcentaje: c.porcentaje,
    color: GASTO_COLORS[i % GASTO_COLORS.length] ?? C.cafeBajito,
  }));

  const mesesLabels = data.meses.map((m) => m.mes_label.slice(0, 3).toUpperCase());

  return (
    <div className="flex flex-col gap-3">
      {isFetching && (
        <div className="flex justify-end">
          <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
        </div>
      )}

      {sinDatos ? (
        <Card className="p-10 flex items-center justify-center text-sm text-muted-foreground">
          Sin información para este periodo
        </Card>
      ) : (
        <>
          {/* KPIs — 6 tarjetas llenando todo el ancho */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
            <Kpi label="Vendido" value={fmt(data.kpis.vendido)} accent="#111111" icon={TrendingUp}
              meta={`${data.totales.pedidos} pedidos en el periodo`} />
            <Kpi label="Cobrado" value={fmt(data.kpis.cobrado)} accent={C.ingresos} icon={WalletCards}
              meta={`${pct(data.indicadores.pct_cobranza)} del vendido`} />
            <Kpi label="Por cobrar" value={fmt(data.kpis.por_cobrar)} accent="#B5821F" icon={Clock3}
              meta={`${pct(data.kpis.vendido > 0 ? (data.kpis.por_cobrar / data.kpis.vendido) * 100 : 0)} del vendido`} />
            <Kpi label="Regalado" value={fmt(data.kpis.regalos)} accent={C.gold} icon={Gift}
              meta={`${pct(data.kpis.vendido > 0 ? (data.kpis.regalos / data.kpis.vendido) * 100 : 0)} del vendido`} />
            <Kpi label="Gastos" value={fmt(data.kpis.gastos)} accent={C.gastos} icon={ReceiptText}
              meta={`${pct(data.kpis.vendido > 0 ? (data.kpis.gastos / data.kpis.vendido) * 100 : 0)} del vendido`} />
            <Kpi label="Utilidad" value={fmt(data.kpis.utilidad)} accent={C.ingresos} icon={BarChart3}
              meta={`${pct(data.indicadores.margen_periodo)} margen`} />
          </div>

          {/* Bloque analítico — exactamente en la composición aprobada */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-3 items-stretch">
            <Card className="h-[390px] sm:h-[430px] lg:h-[410px] overflow-hidden">
  <CardHead title="Evolución del año" />

  <div className="h-[calc(100%-37px)] p-4 sm:px-5 sm:pb-5 sm:pt-4">
    <EvolucionChart
      labels={mesesLabels}
      year={data.anio}
      series={[
        {
          label: "Vendido",
          color: "#111111",
          data: data.meses.map((m) => m.vendido),
        },
        {
          label: "Cobrado",
          color: "#087A5A",
          data: data.meses.map((m) => m.cobrado),
        },
        {
          label: "Gastos",
          color: "#FF5A0A",
          data: data.meses.map((m) => m.gastos),
        },
      ]}
    />
  </div>
</Card>
            <Card className="lg:h-[410px]">
              <CardHead title="Indicadores del año" />
              <div className="p-3 sm:p-4 lg:h-[calc(100%-37px)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2 lg:gap-1.5 lg:h-full">
                  {[
                    {
                      label: "Mejor mes",
                      value: data.indicadores.mejor_mes
                        ? `${data.indicadores.mejor_mes.mes_label} · ${fmt(data.indicadores.mejor_mes.vendido)}`
                        : "—",
                      icon: Award,
                      color: C.ingresos,
                    },
                    {
                      label: "Menor mes",
                      value: data.indicadores.menor_mes
                        ? `${data.indicadores.menor_mes.mes_label} · ${fmt(data.indicadores.menor_mes.vendido)}`
                        : "—",
                      icon: ArrowDownRight,
                      color: C.gastos,
                    },
                    {
                      label: "Ticket promedio",
                      value: fmt(data.indicadores.ticket_promedio),
                      icon: ReceiptText,
                      color: C.cafeFuerte,
                    },
                    {
                      label: "% Cobranza",
                      value: pct(data.indicadores.pct_cobranza),
                      icon: Percent,
                      color: C.ingresos,
                    },
                    {
                      label: "Margen del periodo",
                      value: pct(data.indicadores.margen_periodo),
                      icon: BarChart3,
                      color: C.ingresos,
                    },
                    {
                      label: "Prom. mensual ventas",
                      value: fmt(data.indicadores.promedio_mensual_ventas),
                      icon: CalendarDays,
                      color: C.cafeBajito,
                    },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.label}
                        className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-[#FCFBF8] px-3 py-2.5 lg:py-2"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="size-8 shrink-0 rounded-xl flex items-center justify-center"
                            style={{ background: `${item.color}10` }}
                          >
                            <Icon className="size-4" style={{ color: item.color }} />
                          </div>
                          <span className="text-xs font-semibold text-muted-foreground truncate">
                            {item.label}
                          </span>
                        </div>
                        <span
                          className="text-sm font-extrabold text-right tabular-nums whitespace-nowrap"
                          style={{
                            color: item.label === "Margen del periodo"
                              ? C.ingresos
                              : C.cafeFuerte,
                          }}
                        >
                          {item.value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <Card className="w-full lg:h-[500px]">
              <CardHead title="Ventas por producto" icon={Package} />
              <div className="p-3 sm:p-4 lg:h-[calc(100%-37px)]">
                <BarListCard
                  items={productoItems}
                  totalValue={fmt(data.kpis.vendido)}
                  totalLabel="Total vendido"
                  variant="ventas"
                />
              </div>
            </Card>

            <Card className="w-full lg:h-[500px]">
              <CardHead title="Gastos por categoría" icon={Tags} />
              <div className="p-3 sm:p-4 lg:h-[calc(100%-37px)]">
                <BarListCard
                  items={categoriaItems}
                  totalValue={fmt(data.kpis.gastos)}
                  totalLabel="Total gastos"
                  variant="gastos"
                />
              </div>
            </Card>
          </div>

          {/* Resumen por mes — al final, con encabezado y primera columna fijos */}
          <TablaMensual meses={data.meses} totales={data.totales} anio={data.anio} />
        </>
      )}
    </div>
  );
}

// ─── Vista: todos los años ──────────────────────────────────────────────────────

function VistaTodos() {
  const { data, isLoading, isError, refetch, isFetching } = useReportesTodos();

  if (isLoading) return (
    <div className="flex items-center justify-center py-24 gap-2 text-sm text-muted-foreground">
      <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      Cargando reportes…
    </div>
  );
  if (isError || !data) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <p className="text-sm text-destructive">Error al cargar los reportes.</p>
      <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
    </div>
  );

  const totales: ReportesAnioResumen | null = data.anios.length > 0 ? {
    anio: 0, ...data.kpis,
  } : null;

  const labels = data.anios.map((a) => String(a.anio));

  return (
    <div className="flex flex-col gap-3">
      {isFetching && (
        <div className="flex justify-end">
          <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
        </div>
      )}

      {data.anios.length === 0 ? (
        <Card className="p-10 flex items-center justify-center text-sm text-muted-foreground">
          Sin información para este periodo
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
            <Kpi label="Vendido" value={fmt(data.kpis.vendido)} accent="#111111" icon={TrendingUp} meta={`${data.kpis.pedidos} pedidos`} />
            <Kpi label="Cobrado" value={fmt(data.kpis.cobrado)} accent={C.ingresos} icon={WalletCards} meta={`${pct(data.kpis.pct_cobranza)} de cobranza`} />
            <Kpi label="Por cobrar" value={fmt(data.kpis.por_cobrar)} accent="#B5821F" icon={Clock3} meta="Saldo pendiente" />
            <Kpi label="Regalado" value={fmt(data.kpis.regalos)} accent={C.gold} icon={Gift} meta="Cortesías acumuladas" />
            <Kpi label="Gastos" value={fmt(data.kpis.gastos)} accent={C.gastos} icon={ReceiptText} meta="Gasto acumulado" />
            <Kpi label="Utilidad" value={fmt(data.kpis.utilidad)} accent={C.ingresos} icon={BarChart3} meta="Resultado acumulado" />
          </div>

          <TablaAnios anios={data.anios} totales={totales} />

          <Card>
            <CardHead title="Evolución por año" />
            <div className="p-4">
              <EvolucionChart
                labels={labels}
                series={[
                  { label: "Vendido",  color: "#111111",  data: data.anios.map((a) => a.vendido) },
                  { label: "Cobrado",  color: C.ingresos,  data: data.anios.map((a) => a.cobrado) },
                  { label: "Gastos",   color: C.gastos,    data: data.anios.map((a) => a.gastos) },
                  { label: "Utilidad", color: C.gold,      data: data.anios.map((a) => a.utilidad) },
                ]}
              />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────

export default function ReportesPage() {
  const now = new Date();
  const [seleccion, setSeleccion] = useState<number | "todos">(now.getFullYear());

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y, y + 1, y + 2, y + 3];
  }, [now]);

  return (
    <div className="min-h-full bg-[#F7F4EE] p-4 sm:p-6"><div className="mx-auto w-full max-w-[1600px] space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold text-foreground truncate">Reportes</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Resumen financiero y operativo anual</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <YearSelector value={seleccion} years={years} onChange={setSeleccion} />
        </div>
      </div>

      {seleccion === "todos" ? <VistaTodos /> : <VistaAnio anio={seleccion} />}
    </div></div>
  );
}
